"use client";

import { useRef } from "react";
import Image from "next/image";
import {
	motion,
	useMotionValue,
	useSpring,
	useTransform,
	type MotionValue,
} from "motion/react";

// Every Chromium-based browser Screenlet runs in. Drop a new icon in
// /public/others and append an entry here — the dock sizes itself.
const BROWSERS = [
	{ name: "Google Chrome", src: "/others/chrome.svg" },
	{ name: "Microsoft Edge", src: "/others/edge.svg" },
	{ name: "Brave", src: "/others/brave.svg" },
	{ name: "Opera", src: "/others/opera.svg" },
	{ name: "Comet", src: "/others/commet_browser.webp" },
];

// Resting icon size (px) and how far the cursor's influence reaches.
const BASE_SIZE = 48;
const MAX_SIZE = 88;
const INFLUENCE = 130;

export function BrowserDock() {
	// Cursor X within the dock. Infinity == cursor is away, so nothing magnifies.
	const mouseX = useMotionValue(Number.POSITIVE_INFINITY);

	return (
		<div className="flex flex-col items-center">
			<motion.div
				onMouseMove={(e) => mouseX.set(e.clientX)}
				onMouseLeave={() => mouseX.set(Number.POSITIVE_INFINITY)}
				style={{ height: BASE_SIZE + 24 }}
				className="border-border/60 bg-card/60 mx-auto flex items-end gap-3 rounded-2xl border px-4 pb-3 shadow-lg backdrop-blur-md"
			>
				{BROWSERS.map((browser) => (
					<DockIcon key={browser.name} mouseX={mouseX} {...browser} />
				))}
			</motion.div>
			<p className="text-muted-foreground mt-5 text-center text-sm">
				Runs on every Chromium browser — Chrome, Edge, Brave, Opera & Comet — on
				Windows, macOS, Linux &amp; ChromeOS.
			</p>
		</div>
	);
}

function DockIcon({
	mouseX,
	name,
	src,
}: {
	mouseX: MotionValue<number>;
	name: string;
	src: string;
}) {
	const ref = useRef<HTMLDivElement>(null);

	// Distance from cursor to this icon's center, in px.
	const distance = useTransform(mouseX, (x) => {
		const bounds = ref.current?.getBoundingClientRect() ?? {
			x: 0,
			width: BASE_SIZE,
		};
		return x - bounds.x - bounds.width / 2;
	});

	// Closer cursor -> bigger icon, tapering off past INFLUENCE.
	const sizeTarget = useTransform(
		distance,
		[-INFLUENCE, 0, INFLUENCE],
		[BASE_SIZE, MAX_SIZE, BASE_SIZE],
	);
	const size = useSpring(sizeTarget, {
		mass: 0.1,
		stiffness: 170,
		damping: 14,
	});

	return (
		<motion.div
			ref={ref}
			style={{ width: size, height: size }}
			className="group relative flex aspect-square items-center justify-center"
		>
			{/* tooltip */}
			<span className="border-border/60 bg-card text-foreground pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 rounded-md border px-2 py-1 text-xs font-medium whitespace-nowrap opacity-0 shadow-sm transition-opacity duration-150 group-hover:opacity-100">
				{name}
			</span>
			<Image
				src={src}
				alt={name}
				width={MAX_SIZE}
				height={MAX_SIZE}
				className="h-3/4 w-3/4 object-contain drop-shadow-sm select-none"
				draggable={false}
			/>
		</motion.div>
	);
}
